import useCreationHistory from "../hooks/useCreationHistory";
import { isResidentialBien, isLandBien } from "../Services/bienEditModel";
import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  LuArrowLeft,
  LuArrowRight,
  LuBath,
  LuBuilding2,
  LuCheck,
  LuCircleDollarSign,
  LuHouse,
  LuImagePlus,
  LuMinus,
  LuCircleParking,
  LuPlus,
  LuSend,
  LuSofa,
  LuUserRound,
} from "react-icons/lu";

import { createBien } from "../Services/biensService.jsx";
import ImageUploadSection from "../components/ImageUploadSection";
import AddressForm from "../components/CreateSectionAdress";

const TOTAL_STEPS = 6;

const STEP_ITEMS = [
  {
    id: 1,
    get label() { return uiI18n.t("ui.type"); },
    icon: LuBuilding2,
  },
  {
    id: 2,
    get label() { return uiI18n.t("ui.description"); },
    icon: LuHouse,
  },
  {
    id: 3,
    get label() { return uiI18n.t("ui.address"); },
    icon: LuBuilding2,
  },
  {
    id: 4,
    get label() { return uiI18n.t("ui.price"); },
    icon: LuCircleDollarSign,
  },
  {
    id: 5,
    get label() { return uiI18n.t("ui.contact"); },
    icon: LuUserRound,
  },
  {
    id: 6,
    get label() { return uiI18n.t("ui.photos_290"); },
    icon: LuImagePlus,
  },
];

const PROPERTY_CATEGORIES = [
  {
    value: "Appartement",
    translationKey: "apartment",
    get fallback() { return uiI18n.t("categories.apartment"); },
  },
  {
    value: "Maison",
    translationKey: "house",
    get fallback() { return uiI18n.t("categories.house"); },
  },
  {
    value: "Commercial",
    translationKey: "commercial",
    get fallback() { return uiI18n.t("categories.commercial"); },
  },
  {
    value: "Terrain",
    translationKey: "land",
    get fallback() { return uiI18n.t("categories.land"); },
  },
  {
    value: "Autres",
    translationKey: "others",
    get fallback() { return uiI18n.t("categories.others"); },
  },
];

function CreateBien() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth();

  const currentUser = auth.currentUser;

  const { step: currentSection, setStep: setCurrentSection, markDirty, markSaved } = useCreationHistory();

  const [chambres, setChambres] = useState(0);
  const [sallesDeBain, setSallesDeBain] =
    useState(0);

  const [submitting, setSubmitting] =
    useState(false);

  const [formData, setFormData] = useState({
    Libelle: "",
    Type: "rent",
    Bien: "",
    Parking: false,
    geolocationEnabled: false,
    meuble: false,
    Chambres: 0,
    SallesBains: 0,
    Delegation: "",
    Latitude: null,
    Longitude: null,
    Ville: "",
    CodePostal: "",
    Pays: "Tunisie",
    Description: "",
    PrixReduit: "",
    Prix: "",
    UrlImages: "",
    Surface: "",
    Offre: false,
    images: [],
    Category: "Appartement",
    Adresse: "",
    Email: currentUser?.email || "",
    Tel: "",
    uid: currentUser?.uid || "",
    name: currentUser?.displayName || "",
  });

  const {
    Libelle,
    Type,
    Parking,
    meuble,
    Latitude,
    Longitude,
    Description,
    PrixReduit,
    Prix,
    Surface,
    Offre,
    images,
    Category,
    Email,
    Tel,
  } = formData;

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setFormData((previousValue) => ({
      ...previousValue,
      Email:
        previousValue.Email ||
        currentUser.email ||
        "",
      uid: currentUser.uid,
      name: currentUser.displayName || "",
    }));
  }, [currentUser]);

  useEffect(() => {
    setFormData((previousValue) => ({
      ...previousValue,
      Chambres: chambres,
      SallesBains: sallesDeBain,
    }));
  }, [chambres, sallesDeBain]);

  const progressPercentage =
    ((currentSection - 1) /
      (TOTAL_STEPS - 1)) *
    100;

  const updateField = (field, value) => {
    setFormData((previousValue) => ({
      ...previousValue,
      [field]: value,
    }));
  };

  const onChange = (event) => {
    const { id, value, type, checked } =
      event.target;

    let normalizedValue = value;

    if (type === "checkbox") {
      normalizedValue = checked;
    } else if (value === "true") {
      normalizedValue = true;
    } else if (value === "false") {
      normalizedValue = false;
    }

    updateField(id, normalizedValue);
  };

  const onImagesChange = (imageNames) => {
    const normalizedImages = Array.isArray(
      imageNames
    )
      ? imageNames
      : [];

    setFormData((previousValue) => ({
      ...previousValue,
      UrlImages: normalizedImages.join(","),
      images: normalizedImages,
    }));
  };

  const validateCurrentStep = () => {
    if (currentSection === 1) {
      if (!Type || !Category) {
        toast.error(
          t(
            "create.select_type_error",
            "Sélectionnez le type de transaction et la catégorie."
          )
        );
        return false;
      }
    }

    if (currentSection === 2) {
      if (!Libelle.trim()) {
        toast.error(
          t(
            "create.title_required",
            "Le titre de l’annonce est obligatoire."
          )
        );
        return false;
      }

      if (
        Surface !== "" && (!Number.isFinite(Number(Surface)) || Number(Surface) < 0)
      ) {
        toast.error(
          t(
            "create.surface_required",
            "Veuillez indiquer une surface valide."
          )
        );
        return false;
      }
    }

    if (currentSection === 3) {
      if (
        !formData.Ville ||
        !formData.Delegation
      ) {
        toast.error(
          t(
            "create.address_fields_required",
            "Veuillez renseigner la ville et la délégation."
          )
        );
        return false;
      }
    }

    if (currentSection === 4) {
      if (Prix !== "" && (!Number.isFinite(Number(Prix)) || Number(Prix) < 0)) {
        toast.error(
          t(
            "create.price_required",
            "Veuillez saisir un prix valide."
          )
        );
        return false;
      }

      if (
        Offre &&
        (!PrixReduit ||
          Number(PrixReduit) <= 0 ||
          Number(PrixReduit) >= Number(Prix))
      ) {
        toast.error(
          t(
            "create.discount_price_error",
            "Le prix réduit doit être inférieur au prix initial."
          )
        );
        return false;
      }
    }

    if (currentSection === 5) {
      if (!Email.trim() || !Tel.trim()) {
        toast.error(
          t(
            "create.contact_required",
            "Veuillez renseigner votre e-mail et votre téléphone."
          )
        );
        return false;
      }
    }

    return true;
  };

  const handleContinue = () => {
    if (!validateCurrentStep()) {
      return;
    }

    setCurrentSection((previousValue) =>
      Math.min(
        previousValue + 1,
        TOTAL_STEPS
      )
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePrevious = () => {
    setCurrentSection((previousValue) =>
      Math.max(previousValue - 1, 1)
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const createClick = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!currentUser?.uid) {
      toast.error(
        t(
          "create.login_required",
          "Vous devez être connecté pour publier une annonce."
        )
      );
      navigate("/SignIn");
      return;
    }

    if (
      !Array.isArray(images) ||
      images.length === 0
    ) {
      toast.error(
        t(
          "create.images_required",
          "Ajoutez au moins une photo."
        )
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        Chambres: chambres,
        SallesBains: sallesDeBain,
        Prix: Prix === "" ? undefined : Number(Prix),
        PrixReduit: Offre
          ? Number(PrixReduit)
          : 0,
        Surface: Surface === "" ? undefined : Number(Surface),
        Latitude: Latitude == null ? undefined : Number(Latitude),
        Longitude: Longitude == null ? undefined : Number(Longitude),
        uid: currentUser.uid,
        Email:
          Email || currentUser.email || "",
        name:
          currentUser.displayName || "",
      };

      const result = await createBien(
        payload,
        images
      );

      toast.success(
        result?.Message ||
          t(
            "create.success",
            "Votre annonce a été publiée avec succès."
          )
      );

      markSaved();
      navigate("/myAds");
    } catch (error) {
      console.error(
        "Erreur lors de la création :",
        error
      );

      toast.error(
        error?.message ||
          t(
            "create.error",
            "Impossible de publier l’annonce."
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-5xl">
        {/* En-tête */}
        <div className="mb-8 text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-green-600">
            Lina Immo
          </p>

          <h1 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
            {t(
              "create_ad",
              "Déposer une annonce"
            )}
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            {t(
              "create.description",
              "Renseignez les informations de votre bien en quelques étapes."
            )}
          </p>
        </div>

        {/* Progression */}
        <div className="mb-7 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6">
          <div className="relative">
            <div className="absolute start-0 end-0 top-5 hidden h-1 rounded-full bg-slate-100 sm:block">
              <div
                className="h-full rounded-full bg-green-600 transition-all duration-500"
                style={{
                  width: `${progressPercentage}%`,
                }}
              />
            </div>

            <div className="relative grid grid-cols-3 gap-3 sm:grid-cols-6">
              {STEP_ITEMS.map((step) => {
                const Icon = step.icon;
                const completed =
                  step.id < currentSection;
                const active =
                  step.id === currentSection;

                return (
                  <div
                    key={step.id}
                    className="flex flex-col items-center text-center"
                  >
                    <span
                      className={`
                        relative
                        z-10
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-full
                        border-2
                        text-sm
                        font-black
                        transition
                        ${
                          completed
                            ? "border-green-600 bg-green-600 text-white"
                            : active
                              ? "border-green-600 bg-white text-green-600 shadow-lg shadow-green-600/15"
                              : "border-slate-200 bg-white text-slate-400"
                        }
                      `}
                    >
                      {completed ? (
                        <LuCheck size={17} />
                      ) : (
                        <Icon size={17} />
                      )}
                    </span>

                    <span
                      className={`
                        mt-2
                        text-[11px]
                        font-bold
                        sm:text-xs
                        ${
                          active
                            ? "text-green-700"
                            : completed
                              ? "text-slate-700"
                              : "text-slate-400"
                        }
                      `}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <form onChangeCapture={markDirty} onClickCapture={(event) => { if (event.target.closest("button")) markDirty(); }}
          id="CreateBien"
          onSubmit={createClick}
          className="rounded-3xl border border-slate-100 bg-white p-5 shadow-xl shadow-slate-900/5 sm:p-8"
        >
          {/* Étape 1 */}
          {currentSection === 1 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 1 })}
              title={t(
                "create.transaction_title",
                "Quel type d’annonce souhaitez-vous publier ?"
              )}
              description={t(
                "create.transaction_description",
                "Choisissez la transaction et le type de propriété."
              )}
            >
              <FieldGroup
                label={t(
                  "sell / rent",
                  "Vente ou location"
                )}
              >
                <div className="grid grid-cols-2 gap-3">
                  <ChoiceButton
                    active={Type === "sale"}
                    onClick={() =>
                      updateField("Type", "sale")
                    }
                    icon="🏷️"
                    label={t("sell", "Vendre")}
                  />

                  <ChoiceButton
                    active={Type === "rent"}
                    onClick={() =>
                      updateField("Type", "rent")
                    }
                    icon="🔑"
                    label={t("rent", "Louer")}
                  />
                </div>
              </FieldGroup>

              <FieldGroup
                label={t(
                  "choose_property_type",
                  "Type de bien"
                )}
              >
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {PROPERTY_CATEGORIES.map(
                    (item) => (
                      <ChoiceButton
                        key={item.value}
                        active={
                          Category === item.value
                        }
                        onClick={() =>
                          updateField(
                            "Category",
                            item.value
                          )
                        }
                        label={t(
                          item.translationKey,
                          item.fallback
                        )}
                        compact
                      />
                    )
                  )}
                </div>
              </FieldGroup>

              <StepActions
                onNext={handleContinue}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 2 */}
          {currentSection === 2 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 2 })}
              title={t(
                "create.describe_title",
                "Décrivez votre bien"
              )}
              description={t(
                "create.describe_description",
                "Ajoutez un titre clair et les caractéristiques essentielles."
              )}
            >
              <div className="grid gap-5 md:grid-cols-2">
                <FormField
                  label={t(
                    "label",
                    "Titre de l’annonce"
                  )}
                  htmlFor="Libelle"
                  required
                >
                  <input
                    required
                    type="text"
                    id="Libelle"
                    value={Libelle}
                    onChange={onChange}
                    placeholder={uiI18n.t("ui.e_g_bright_apartment_in_tunis")}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  label={t(
                    "surface",
                    "Surface"
                  )}
                  htmlFor="Surface"
                >
                  <div className="relative">
                    <input
                      type="number"
                      id="Surface"
                      min="0"
                      value={Surface}
                      onChange={onChange}
                      placeholder="120"
                      className={`${inputClassName} pe-14`}
                    />

                    <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                      m²
                    </span>
                  </div>
                </FormField>

                <FormField
                  label={t(
                    "description",
                    "Description"
                  )}
                  htmlFor="Description"
                  className="md:col-span-2"
                >
                  <textarea
                    id="Description"
                    value={Description}
                    onChange={onChange}
                    rows={5}
                    placeholder={uiI18n.t("ui.describe_the_highlights_of_your_property")}
                    className={`${inputClassName} min-h-[140px] resize-y py-3`}
                  />
                </FormField>
              </div>

              {!isLandBien(Category) && (
                <>
                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    {isResidentialBien(Category) && (
                    <Counter
                      label={t(
                        "rooms.bedrooms",
                        "Chambres"
                      )}
                      value={chambres}
                      onDecrease={() =>
                        setChambres((value) =>
                          Math.max(0, value - 1)
                        )
                      }
                      onIncrease={() =>
                        setChambres(
                          (value) => value + 1
                        )
                      }
                      icon={LuHouse}
                    />
                    )}

                    <Counter
                      label={t(
                        "listing.bathrooms",
                        "Salles de bain"
                      )}
                      value={sallesDeBain}
                      onDecrease={() =>
                        setSallesDeBain(
                          (value) =>
                            Math.max(
                              0,
                              value - 1
                            )
                        )
                      }
                      onIncrease={() =>
                        setSallesDeBain(
                          (value) => value + 1
                        )
                      }
                      icon={LuBath}
                    />
                  </div>

                  <div className="mt-6 grid gap-5 md:grid-cols-2">
                    <BooleanChoice
                      label={t(
                        "parking",
                        "Parking"
                      )}
                      value={Parking}
                      onChange={(value) =>
                        updateField("Parking",
                          value
                        )
                      }
                      icon={LuCircleParking}
                    />

                    {isResidentialBien(Category) && (
                    <BooleanChoice
                      label={t(
                        "furnished",
                        "Meublé"
                      )}
                      value={meuble}
                      onChange={(value) =>
                        updateField(
                          "meuble",
                          value
                        )
                      }
                      icon={LuSofa}
                    />
                    )}
                  </div>
                </>
              )}

              <StepActions
                onPrevious={handlePrevious}
                onNext={handleContinue}
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

     {/* Étape 3 */}
{currentSection === 3 && (
  <StepContainer
    eyebrow={t("edit.step", { number: 3 })}
    title={t(
      "create.address_title",
      "Localisez votre bien"
    )}
    description={t(
      "create.address_description",
      "Indiquez l'adresse exacte afin que les acheteurs puissent facilement situer votre bien."
    )}
  >
    <div className="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
      <AddressForm
        formData={formData}
        setFormData={setFormData}
        handlePrevious={handlePrevious}
        handleContinue={handleContinue}
      />
    </div>
  </StepContainer>
)}

          {/* Étape 4 */}
          {currentSection === 4 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 4 })}
              title={t(
                "create.price_title",
                "Définissez le prix"
              )}
              description={t(
                "create.price_description",
                "Indiquez le prix du bien et ajoutez éventuellement une promotion."
              )}
            >
              <BooleanChoice
                label={t(
                  "offer",
                  "Offre promotionnelle"
                )}
                value={Offre}
                onChange={(value) => {
                  updateField("Offre",
                    value
                  );

                  if (!value) {
                    updateField(
                      "PrixReduit",
                      ""
                    );
                  }
                }}
                icon={LuCircleDollarSign}
              />

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <FormField
                  label={t(
                    "price",
                    "Prix"
                  )}
                  htmlFor="Prix"
                >
                  <PriceInput
                    id="Prix"
                    value={Prix}
                    onChange={onChange}
                  />
                </FormField>

                {Offre && (
                  <FormField
                    label={t(
                      "create.discount_price",
                      "Prix réduit"
                    )}
                    htmlFor="PrixReduit"
                    required
                  >
                    <PriceInput
                      id="PrixReduit"
                      value={PrixReduit}
                      onChange={onChange}
                    />
                  </FormField>
                )}
              </div>

              <StepActions
                onPrevious={handlePrevious}
                onNext={handleContinue}
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 5 */}
          {currentSection === 5 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 5 })}
              title={t(
                "create.contact_title",
                "Vos coordonnées"
              )}
              description={t(
                "create.contact_description",
                "Ces informations permettront aux intéressés de vous contacter."
              )}
            >
              <div className="grid gap-5 md:grid-cols-2">
                <FormField
                  label={t(
                    "email",
                    "E-mail"
                  )}
                  htmlFor="Email"
                  required
                >
                  <input
                    required
                    type="email"
                    id="Email"
                    value={Email}
                    onChange={onChange}
                    autoComplete="email"
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  label={t(
                    "numtel",
                    "Téléphone"
                  )}
                  htmlFor="Tel"
                  required
                >
                  <input
                    required
                    type="tel"
                    id="Tel"
                    value={Tel}
                    onChange={(event) => {
                      const normalized =
                        event.target.value
                          .replace(
                            /[^0-9+\s]/g,
                            ""
                          )
                          .slice(0, 20);

                      updateField(
                        "Tel",
                        normalized
                      );
                    }}
                    autoComplete="tel"
                    placeholder="+216 00 000 000"
                    className={inputClassName}
                  />
                </FormField>
              </div>

              <StepActions
                onPrevious={handlePrevious}
                onNext={handleContinue}
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 6 */}
          {currentSection === 6 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 6 })}
              title={t(
                "create.photos_title",
                "Ajoutez vos photos"
              )}
              description={t(
                "create.photos_description",
                "Ajoutez des photos lumineuses et nettes pour mettre votre bien en valeur."
              )}
            >
              <ImageUploadSection
                onImagesChange={
                  onImagesChange
                }
                handlePrevious={
                  handlePrevious
                }
              />

              <div className="mt-7 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className={secondaryButtonClass}
                >
                  <LuArrowLeft size={17} />
                  {t(
                    "previous",
                    "Précédent"
                  )}
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    bg-green-600
                    px-7
                    text-sm
                    font-black
                    text-white
                    shadow-lg
                    shadow-green-600/20
                    transition-all
                    hover:-translate-y-0.5
                    hover:bg-green-700
                    disabled:cursor-not-allowed
                    disabled:bg-slate-300
                    disabled:shadow-none
                    focus:outline-none
                    focus:ring-4
                    focus:ring-green-500/20
                  "
                >
                  {submitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      {t(
                        "sending",
                        "Publication..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuSend size={17} />
                      {t(
                        "create.publish",
                        "Publier l’annonce"
                      )}
                    </>
                  )}
                </button>
              </div>
            </StepContainer>
          )}
        </form>
      </div>
    </main>
  );
}

const inputClassName = `
  h-12
  w-full
  rounded-2xl
  border
  border-slate-200
  bg-white
  px-4
  text-sm
  font-medium
  text-slate-900
  outline-none
  transition
  placeholder:text-slate-400
  hover:border-slate-300
  focus:border-green-500
  focus:ring-4
  focus:ring-green-500/10
`;

const secondaryButtonClass = `
  inline-flex
  h-12
  items-center
  justify-center
  gap-2
  rounded-2xl
  border
  border-slate-200
  bg-white
  px-6
  text-sm
  font-bold
  text-slate-700
  transition
  hover:border-slate-300
  hover:bg-slate-100
  focus:outline-none
  focus:ring-4
  focus:ring-slate-500/10
`;

function StepContainer({
  eyebrow,
  title,
  description,
  children,
}) {
  useUiTranslation();
  return (
    <section>
      <div className="mb-7">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600">
          {eyebrow}
        </p>

        <h2 className="mt-2 text-2xl font-black text-slate-950">
          {title}
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-500">
          {description}
        </p>
      </div>

      {children}
    </section>
  );
}

function FieldGroup({
  label,
  children,
}) {
  useUiTranslation();
  return (
    <div className="mb-7">
      <p className="mb-3 text-sm font-black text-slate-700">
        {label}
      </p>

      {children}
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  required,
  className = "",
  children,
}) {
  useUiTranslation();
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="mb-2 block text-sm font-black text-slate-700"
      >
        {label}

        {required && (
          <span className="ms-1 text-red-500">
            *
          </span>
        )}
      </label>

      {children}
    </div>
  );
}

function ChoiceButton({
  active,
  onClick,
  icon,
  label,
  compact = false,
}) {
  useUiTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex
        items-center
        justify-center
        gap-2
        rounded-2xl
        border
        font-bold
        transition-all
        duration-200
        focus:outline-none
        focus:ring-4
        focus:ring-green-500/15
        ${
          compact
            ? "min-h-12 px-3 py-3 text-xs sm:text-sm"
            : "min-h-14 px-5 py-4 text-sm"
        }
        ${
          active
            ? "border-green-600 bg-green-600 text-white shadow-lg shadow-green-600/15"
            : "border-slate-200 bg-white text-slate-700 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
        }
      `}
    >
      {icon && (
        <span className="text-lg">
          {icon}
        </span>
      )}

      {label}
    </button>
  );
}

function Counter({
  label,
  value,
  onDecrease,
  onIncrease,
  icon: Icon,
}) {
  useUiTranslation();
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <span className="flex items-center gap-2 text-sm font-black text-slate-700">
        <Icon
          size={18}
          className="text-green-600"
        />

        {label}
      </span>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onDecrease}
          aria-label={uiI18n.t("ui.decrease", { label: label })}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:border-green-300 hover:text-green-600"
        >
          <LuMinus size={16} />
        </button>

        <span className="min-w-6 text-center text-lg font-black text-slate-900">
          {value}
        </span>

        <button
          type="button"
          onClick={onIncrease}
          aria-label={uiI18n.t("ui.increase", { label: label })}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-white transition hover:bg-green-700"
        >
          <LuPlus size={16} />
        </button>
      </div>
    </div>
  );
}

function BooleanChoice({
  label,
  value,
  onChange,
  icon: Icon,
}) {
  useUiTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="mb-3 flex items-center gap-2 text-sm font-black text-slate-700">
        <Icon
          size={18}
          className="text-green-600"
        />

        {label}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`
            h-11
            rounded-xl
            text-sm
            font-bold
            transition
            ${
              value
                ? "bg-green-600 text-white"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-green-50"
            }
          `}
        > {uiI18n.t("ui.yes")} </button>

        <button
          type="button"
          onClick={() => onChange(false)}
          className={`
            h-11
            rounded-xl
            text-sm
            font-bold
            transition
            ${
              !value
                ? "bg-slate-900 text-white"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
            }
          `}
        > {uiI18n.t("ui.no")} </button>
      </div>
    </div>
  );
}

function PriceInput({
  id,
  value,
  onChange,
}) {
  useUiTranslation();
  return (
    <div className="relative">
      <input
        required={id === "PrixReduit"}
        type="number"
        id={id}
        value={value}
        onChange={onChange}
        min="0"
        max="50000000"
        placeholder="0"
        className={`${inputClassName} pe-20 text-lg font-black`}
      />

      <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">
        TND
      </span>
    </div>
  );
}

function StepActions({
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}) {
  useUiTranslation();
  return (
    <div
      className={`
        mt-8
        flex
        flex-col-reverse
        gap-3
        border-t
        border-slate-100
        pt-6
        sm:flex-row
        ${
          onPrevious
            ? "sm:justify-between"
            : "sm:justify-end"
        }
      `}
    >
      {onPrevious && (
        <button
          type="button"
          onClick={onPrevious}
          className={secondaryButtonClass}
        >
          <LuArrowLeft size={17} />
          {previousLabel}
        </button>
      )}

      {onNext && (
        <button
          type="button"
          onClick={onNext}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            bg-slate-950
            px-7
            text-sm
            font-black
            text-white
            shadow-lg
            transition-all
            hover:-translate-y-0.5
            hover:bg-green-600
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/20
          "
        >
          {nextLabel}
          <LuArrowRight
            size={17}
            className="rtl:rotate-180"
          />
        </button>
      )}
    </div>
  );
}

export default CreateBien;
